import React, { useState } from 'react';
import DefaultlayoutHOC from '../layouts/Default.layout';
import CatalogListRow from '../components/catalog/CatalogListRow.component';
import InfiniteFeed from '../components/InfiniteFeed/InfiniteFeed.component';
import useInfiniteFeed from '../hooks/useInfiniteFeed';
import { tvFeed, TV_SORT_OPTIONS } from '../services/tmdbFeed';
import { FaSortAmountDown, FaStar, FaTh, FaList, FaUndo, FaTv } from 'react-icons/fa';

const TV_GENRES = [
  { id: 'all', name: 'All Genres' },
  { id: 18, name: 'Drama' },
  { id: 10765, name: 'Sci-Fi & Fantasy' },
  { id: 10759, name: 'Action & Adventure' },
  { id: 35, name: 'Comedy' },
  { id: 80, name: 'Crime' },
  { id: 9648, name: 'Mystery' },
  { id: 16, name: 'Animation' },
  { id: 99, name: 'Documentary' },
];

const RATING_OPTIONS = [0, 7, 8, 8.5];

const SeriesPage = () => {
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [selectedSort, setSelectedSort] = useState('newest');
  const [minRating, setMinRating] = useState(0);
  const [viewMode, setViewMode] = useState('grid');

  // Freshly-aired Indian series first, then TMDB page 2, 3, … appended on scroll.
  const feed = useInfiniteFeed({
    kind: 'tv',
    fetcher: tvFeed.fetch,
    sort: selectedSort,
    genreId: selectedGenre,
    minRating,
  });

  const resetFilters = () => {
    setSelectedGenre('all');
    setSelectedSort('newest');
    setMinRating(0);
  };

  const isFiltered = selectedGenre !== 'all' || selectedSort !== 'newest' || minRating > 0;
  const isList = viewMode === 'list';

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      {/* Header Banner */}
      <div className="mb-8 text-center sm:text-left bg-gradient-to-r from-accent-gold/10 via-dark-800 to-dark-800 border border-white/10 rounded-3xl p-6 sm:p-10 shadow-2xl relative overflow-hidden">
        <div className="max-w-2xl relative z-10">
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-2">
            <span className="bg-accent-gold text-dark-900 font-extrabold text-xs px-3 py-1 rounded-full uppercase tracking-wider">
              TV Shows &amp; Series
            </span>
            <span className="text-xs text-gray-400 font-medium">
              {feed.totalResults > 0
                ? `${feed.totalResults.toLocaleString()}+ Series via TMDB`
                : 'Indexed via TMDB'}
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-2">
            TV Series Catalog &amp; Episodes
          </h1>
          <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">
            Hindi &amp; Gujarati series, most recently aired first. Scroll to keep loading more
            episodes and shows.
          </p>
        </div>
      </div>

      {/* Control Bar: Genre Chips */}
      <div className="mb-6 space-y-4">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {TV_GENRES.map((genre) => {
            const isSelected = String(selectedGenre) === String(genre.id);
            return (
              <button
                key={genre.id}
                type="button"
                onClick={() => setSelectedGenre(String(genre.id))}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all border ${
                  isSelected
                    ? 'bg-accent-gold text-dark-900 border-accent-gold font-bold shadow-md shadow-accent-gold/20'
                    : 'bg-dark-700 text-gray-300 border-white/10 hover:border-accent-gold/40 hover:text-white'
                }`}
              >
                {genre.name}
              </button>
            );
          })}
        </div>

        {/* Secondary Filter Row: Sort, Min Rating, View Toggle, Reset */}
        <div className="bg-dark-800/80 rounded-2xl border border-white/10 p-3 sm:p-4 flex flex-wrap items-center justify-between gap-3 shadow-lg">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 bg-dark-700 px-3 py-1.5 rounded-xl border border-white/10 text-xs">
              <FaSortAmountDown className="text-accent-gold w-3.5 h-3.5" aria-hidden="true" />
              <span className="text-gray-400">Sort:</span>
              <select
                aria-label="Sort TV series"
                value={selectedSort}
                onChange={(e) => setSelectedSort(e.target.value)}
                className="bg-transparent text-white font-medium focus:outline-none cursor-pointer"
              >
                {TV_SORT_OPTIONS.map((opt) => (
                  <option key={opt.id} value={opt.id} className="bg-dark-800 text-white">
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2 bg-dark-700 px-3 py-1.5 rounded-xl border border-white/10 text-xs">
              <FaStar className="text-accent-gold w-3.5 h-3.5" aria-hidden="true" />
              <span className="text-gray-400">Rating:</span>
              <select
                aria-label="Minimum rating"
                value={minRating}
                onChange={(e) => setMinRating(Number(e.target.value))}
                className="bg-transparent text-white font-medium focus:outline-none cursor-pointer"
              >
                {RATING_OPTIONS.map((val) => (
                  <option key={val} value={val} className="bg-dark-800 text-white">
                    {val === 0 ? 'All Ratings' : `${val}.0+ Rated`}
                  </option>
                ))}
              </select>
            </div>

            {isFiltered && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 text-xs text-accent-gold hover:text-accent-goldHover transition-colors px-2 py-1"
              >
                <FaUndo className="w-3 h-3" aria-hidden="true" />
                <span>Reset</span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">
              Loaded <strong className="text-white">{feed.items.length}</strong> of{' '}
              <strong className="text-accent-gold">{feed.totalResults.toLocaleString()}</strong> series
            </span>

            <div className="flex items-center bg-dark-700 rounded-xl p-1 border border-white/10">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                aria-pressed={viewMode === 'grid'}
                className={`p-1.5 rounded-lg transition-colors ${
                  viewMode === 'grid' ? 'bg-accent-gold text-dark-900 shadow' : 'text-gray-400 hover:text-white'
                }`}
                title="Grid View"
              >
                <FaTh className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                aria-pressed={isList}
                className={`p-1.5 rounded-lg transition-colors ${
                  isList ? 'bg-accent-gold text-dark-900 shadow' : 'text-gray-400 hover:text-white'
                }`}
                title="Detailed List View"
              >
                <FaList className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <InfiniteFeed
        items={feed.items}
        status={feed.status}
        error={feed.error}
        hasMore={feed.hasMore}
        totalResults={feed.totalResults}
        isInitialLoading={feed.isInitialLoading}
        isLoadingMore={feed.isLoadingMore}
        onLoadMore={feed.loadMore}
        onRetry={feed.retry}
        label="Indian TV series catalog"
        noun="series"
        skeletonCount={isList ? 4 : 12}
        gridClassName={
          isList ? 'space-y-4' : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 sm:gap-6'
        }
        renderItem={
          isList
            ? (series) => (
                <CatalogListRow item={series} mediaType="tv" detailPath={`/series/${series.id}`} />
              )
            : undefined
        }
        emptyState={
          <div className="text-center py-20 bg-dark-800/60 rounded-3xl border border-white/10 p-8 shadow-2xl">
            <FaTv className="w-12 h-12 text-gray-600 mx-auto mb-3" aria-hidden="true" />
            <h3 className="text-xl font-bold text-white mb-1">No TV Series match your filters</h3>
            <p className="text-gray-400 text-xs sm:text-sm max-w-md mx-auto mb-6 leading-relaxed">
              Try adjusting your genre selection or minimum rating threshold to explore more
              television series.
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-2 bg-accent-gold hover:bg-accent-goldHover text-dark-900 font-extrabold text-xs px-6 py-2.5 rounded-xl transition-all shadow-md"
            >
              <FaUndo className="w-3 h-3" aria-hidden="true" />
              <span>Reset All Filters</span>
            </button>
          </div>
        }
      />
    </div>
  );
};

export default DefaultlayoutHOC(SeriesPage);
